/**
 * Auth-specific visual constants.
 *
 * The hero panel is intentionally dark regardless of the app's light/dark mode.
 * These values live here so components reference named constants, not inline literals.
 */

import { GEIST_MONO } from '../theme/typography';

/** Three-stop gradient used on the dark left hero panel. */
export const HERO_GRADIENT =
  'linear-gradient(150deg, #0E141F 0%, #131D33 45%, #1A2A56 100%)';

/** Radial accent bloom at the bottom-right of the hero panel (Indigo glow). */
export const HERO_RADIAL_ACCENT =
  'radial-gradient(ellipse 60% 50% at 80% 110%, rgba(46,85,230,0.22), transparent 60%)';

// ─── Hero text alphas — white-on-dark opacities ───────────────────────────────
export const HERO_WHITE          = '#fff';                   // full white
export const HERO_WHITE_95       = 'rgba(255,255,255,0.95)'; // brand lockup wordmark
export const HERO_TEXT_SECONDARY = 'rgba(255,255,255,0.78)'; // body text
export const HERO_TEXT_MUTED     = 'rgba(255,255,255,0.55)'; // dimmed tagline span
export const HERO_TEXT_EYEBROW   = 'rgba(255,255,255,0.5)';  // eyebrow / footer labels
export const HERO_TEXT_META      = 'rgba(255,255,255,0.4)';  // footer meta
export const HERO_ICON_BG        = 'rgba(255,255,255,0.06)'; // icon tile background
export const HERO_ICON_BORDER    = 'rgba(255,255,255,0.1)';  // icon tile border
export const HERO_ICON_COLOR     = 'rgba(255,255,255,0.85)'; // icon fill

// ─── Hero piece inner panel ───────────────────────────────────────────────────
export const HERO_PANEL_BG     = 'rgba(255,255,255,0.04)';
export const HERO_PANEL_BORDER = 'rgba(255,255,255,0.08)';

// ─── Dot navigation indicators ───────────────────────────────────────────────
export const HERO_DOT_ACTIVE   = 'rgba(255,255,255,0.7)';
export const HERO_DOT_INACTIVE = 'rgba(255,255,255,0.2)';

// ─── Gantt / project bar overlays (dark-hero-specific pastel tints) ──────────
// Index matches bar palette 1–7 (bar-8 unused in the hero pieces).
export const HERO_BAR_COLORS = [
  'rgba(199,214,245,0.85)', // bar-1 blue
  'rgba(197,228,220,0.85)', // bar-2 teal
  'rgba(230,210,238,0.85)', // bar-3 lavender
  'rgba(244,220,199,0.85)', // bar-4 orange
  'rgba(214,227,207,0.85)', // bar-5 sage
  'rgba(241,214,211,0.85)', // bar-6 rose
  'rgba(199,226,238,0.85)', // bar-7 sky
] as const;

// ─── Workload utilisation tones ───────────────────────────────────────────────
export const HERO_WORKLOAD_OK   = 'rgba(95,176,136,0.9)';
export const HERO_WORKLOAD_WARN = 'rgba(230,185,92,0.95)';
export const HERO_WORKLOAD_LOW  = 'rgba(199,214,245,0.85)';

// ─── Skills matrix dot fill/empty ────────────────────────────────────────────
export const HERO_SKILL_FILLED = 'rgba(199,214,245,0.85)';
export const HERO_SKILL_EMPTY  = 'rgba(255,255,255,0.1)';

// ─── Geist Mono for hero piece metadata rows ─────────────────────────────────
export const HERO_FONT_MONO = GEIST_MONO;
